import { useMemo, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { CollapsePanel } from '@/components/ui/CollapsePanel'
import { Skeleton } from '@/components/ui/skeleton'
import {
  ALTARO_BACKUP_SLUG,
  ALTARO_BACKUP_TAB_DESCRIPTION,
  ALTARO_EVENT_IDS,
  altaroBackupSnapshotFromRmmData,
  altaroStatusBadgeVariant,
  altaroStatusLabel,
  asAltaroEventArray,
  normaliseAltaroStatus,
  scoreAltaroPayload,
} from '@/lib/altaroBackup'
import { CollectedAtEmphasis, joinCollapsedSummary } from '@/components/SnapshotCard'
import { formatDateOnly, formatDateTime } from '@/lib/formatAgent'
import {
  assessmentChecks,
  checkStatusLabel,
  checkStatusVariant,
  normaliseToolkitStatus,
  toolkitStatusLabel,
} from '@/lib/toolkitHealth'
import { cn } from '@/lib/utils'

function statusPillVariant(status) {
  const normalised = normaliseToolkitStatus(status)
  if (normalised === 'PASS') return 'ready'
  if (normalised === 'WARN') return 'pending'
  if (normalised === 'FAIL') return 'destructive'
  return 'inactive'
}

function CountTile({ label, value, variant, active, onClick }) {
  const clickable = Boolean(onClick) && (value ?? 0) > 0
  const Tag = clickable ? 'button' : 'div'

  return (
    <Tag
      type={clickable ? 'button' : undefined}
      onClick={clickable ? onClick : undefined}
      className={cn(
        'rounded-md border border-border px-3 py-2 text-left transition-colors',
        clickable && 'cursor-pointer hover:border-primary/60 hover:bg-muted/40',
        active && 'border-primary bg-primary/5 ring-1 ring-primary/20',
        !clickable && 'opacity-90',
      )}
    >
      <p className="text-xs text-muted-foreground">{label}</p>
      {variant ? (
        <Badge variant={variant} className="mt-1">{value ?? 0}</Badge>
      ) : (
        <p className="mt-1 text-lg font-semibold tabular-nums">{value ?? 0}</p>
      )}
      {clickable && (
        <p className="mt-1 text-[11px] text-muted-foreground">Click to filter events</p>
      )}
    </Tag>
  )
}

function VmSummaryTable({ rows }) {
  if (!rows.length) {
    return (
      <p className="text-sm text-muted-foreground">
        No guest VM backup events found in the lookback window.
      </p>
    )
  }

  return (
    <div className="rounded-md border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
            <th className="px-3 py-2 font-medium">Guest VM</th>
            <th className="px-3 py-2 font-medium">Last backup</th>
            <th className="px-3 py-2 font-medium">Backup status</th>
            <th className="px-3 py-2 font-medium">Last offsite</th>
            <th className="px-3 py-2 font-medium">Latest activity</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={`${row.vm_name || 'unknown'}-${index}`}
              className="border-b border-border last:border-0 align-top"
            >
              <td className="px-3 py-2 font-medium">
                <span className="inline-flex flex-wrap items-center gap-2">
                  {row.vm_name || 'Unknown VM'}
                  {row.is_backup_host ? (
                    <Badge variant="secondary">Backup host</Badge>
                  ) : null}
                </span>
              </td>
              <td className="px-3 py-2 whitespace-nowrap tabular-nums">
                {formatDateTime(row.last_backup_at)}
              </td>
              <td className="px-3 py-2 whitespace-nowrap">
                {row.last_backup_status ? (
                  <Badge
                    variant={
                      row.is_backup_host
                        ? 'secondary'
                        : altaroStatusBadgeVariant(row.last_backup_status)
                    }
                  >
                    {row.is_backup_host
                      ? `${altaroStatusLabel(row.last_backup_status, row.last_backup_status_label)} (ignored)`
                      : altaroStatusLabel(
                          row.last_backup_status,
                          row.last_backup_status_label,
                        )}
                  </Badge>
                ) : (
                  '—'
                )}
              </td>
              <td className="px-3 py-2 whitespace-nowrap">
                {row.last_offsite_at ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="tabular-nums">{formatDateTime(row.last_offsite_at)}</span>
                    {row.last_offsite_status && (
                      <Badge variant={altaroStatusBadgeVariant(row.last_offsite_status)}>
                        {altaroStatusLabel(row.last_offsite_status)}
                      </Badge>
                    )}
                  </span>
                ) : (
                  '—'
                )}
              </td>
              <td className="px-3 py-2">
                <div className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={altaroStatusBadgeVariant(row.last_status)}>
                      {altaroStatusLabel(row.last_status, row.last_status_label)}
                    </Badge>
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {formatDateTime(row.last_event_at)}
                    </span>
                  </div>
                  {row.last_result_summary && (
                    <p className="text-xs text-muted-foreground break-words">
                      {row.last_result_summary}
                    </p>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function RecentEventsList({ events }) {
  if (!events.length) {
    return <p className="text-sm text-muted-foreground">No recent Altaro events to show.</p>
  }

  const grouped = events.reduce((acc, event) => {
    const date = event.occurred_at ? event.occurred_at.slice(0, 10) : 'unknown'
    if (!acc[date]) acc[date] = []
    acc[date].push(event)
    return acc
  }, {})

  return (
    <div className="space-y-4">
      {Object.entries(grouped).map(([date, dayEvents]) => (
        <div key={date}>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
            {formatDateOnly(date)}
          </p>
          <div className="rounded-md border border-border divide-y divide-border">
            {dayEvents.map((event, index) => (
              <div
                key={`${event.occurred_at}-${event.event_id}-${index}`}
                className="px-3 py-3 space-y-1"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={altaroStatusBadgeVariant(event.status)}>
                    {altaroStatusLabel(event.status, event.status_label)}
                  </Badge>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {formatDateTime(event.occurred_at)}
                  </span>
                  <Badge variant="secondary">ID {event.event_id ?? '—'}</Badge>
                  {event.category && (
                    <Badge variant="outline" className="capitalize">{event.category}</Badge>
                  )}
                </div>
                <p className="text-sm font-medium">{event.vm_name || 'Unknown VM'}</p>
                {event.result_summary && (
                  <p className="text-sm text-muted-foreground break-words">{event.result_summary}</p>
                )}
                {event.message && (
                  <p className="text-xs text-muted-foreground break-words">{event.message}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

function ChecksTable({ checks }) {
  const rows = Array.isArray(checks) ? checks : []
  if (rows.length === 0) return null

  return (
    <div className="pt-3">
      <p className="text-xs font-medium text-muted-foreground mb-2">Checks</p>
      <div className="rounded-md border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
              <th className="px-3 py-2 font-medium">Area</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium">Detail</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((check, index) => (
              <tr
                key={`${check.area}-${index}`}
                className="border-b border-border last:border-0 align-top"
              >
                <td className="px-3 py-2 font-medium">{check.area || '—'}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <Badge variant={checkStatusVariant(check.status)}>
                    {checkStatusLabel(check.status)}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-muted-foreground">{check.detail || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export function AgentBackup({ loading, data, hostname }) {
  const [openSections, setOpenSections] = useState({
    summary: false,
    vms: false,
    events: false,
    checks: false,
  })
  const [statusFilter, setStatusFilter] = useState('all')

  const snapshot = altaroBackupSnapshotFromRmmData(data)
  const rawPayload = snapshot?.payload ?? null
  const scored = useMemo(
    () => scoreAltaroPayload(rawPayload, { hostname }),
    [rawPayload, hostname],
  )
  const payload = useMemo(() => {
    if (!rawPayload) return null
    return {
      ...rawPayload,
      overall_status: scored.overall_status ?? rawPayload.overall_status,
      interpretation: scored.interpretation ?? rawPayload.interpretation,
      vm_summary: scored.vm_summary,
    }
  }, [rawPayload, scored])
  const checks = assessmentChecks(payload)
  const vmSummary = useMemo(
    () => asAltaroEventArray(payload?.vm_summary),
    [payload?.vm_summary],
  )
  const recentEvents = useMemo(
    () => asAltaroEventArray(payload?.recent_events),
    [payload?.recent_events],
  )
  const filteredEvents = useMemo(() => {
    if (statusFilter === 'all') return recentEvents
    return recentEvents.filter(
      (event) => normaliseAltaroStatus(event.status) === statusFilter,
    )
  }, [recentEvents, statusFilter])

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  if (!snapshot) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">{ALTARO_BACKUP_TAB_DESCRIPTION}</p>
        <Card className="border-dashed">
          <CardContent className="pt-6 text-sm text-muted-foreground">
            No Altaro backup data for this device yet. Deploy the {ALTARO_BACKUP_SLUG} RMM script
            from Atera (run as System on the Altaro backup host) to populate this tab.
          </CardContent>
        </Card>
      </div>
    )
  }

  const counts = payload?.counts ?? {}
  const collapsedHeader = joinCollapsedSummary([
    payload?.interpretation,
    payload?.script_version ? `Script v${payload.script_version}` : null,
    snapshot.collected_at ? (
      <CollectedAtEmphasis collectedAt={snapshot.collected_at} />
    ) : null,
  ])

  function setSectionOpen(key, open) {
    setOpenSections((prev) => ({ ...prev, [key]: open }))
  }

  function selectStatusFilter(next) {
    setStatusFilter((current) => (current === next ? 'all' : next))
    setSectionOpen('events', true)
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{ALTARO_BACKUP_TAB_DESCRIPTION}</p>

      <CollapsePanel
        id="altaro-backup-overview"
        title="Altaro VM Backup"
        collapsedSummary={collapsedHeader}
        open={openSections.summary}
        onOpenChange={(open) => setSectionOpen('summary', open)}
        pill={payload?.overall_status ? toolkitStatusLabel(payload.overall_status) : undefined}
        pillVariant={statusPillVariant(payload?.overall_status)}
      >
        <p className="mb-4 text-xs text-muted-foreground">
          {payload?.script_version ? (
            <span>Script version {payload.script_version}</span>
          ) : (
            <span>Script version not reported</span>
          )}
          {snapshot.collected_at && (
            <span>
              {' · '}
              <CollectedAtEmphasis collectedAt={snapshot.collected_at} />
            </span>
          )}
          {payload?.lookback_days != null && (
            <span>{' · '}Lookback {payload.lookback_days} days</span>
          )}
        </p>
        {payload?.interpretation && (
          <p className="text-sm text-muted-foreground mb-4">{payload.interpretation}</p>
        )}
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <CountTile
            label="Successful backups"
            value={counts.successful_backup}
            variant="success"
            active={statusFilter === 'success'}
            onClick={() => selectStatusFilter('success')}
          />
          <CountTile
            label="Backup warnings"
            value={counts.warning_backup}
            variant="warning"
            active={statusFilter === 'warning'}
            onClick={() => selectStatusFilter('warning')}
          />
          <CountTile
            label="Failed backups"
            value={counts.failed_backup}
            variant="destructive"
            active={statusFilter === 'failed'}
            onClick={() => selectStatusFilter('failed')}
          />
          <CountTile
            label="Guest VMs tracked"
            value={vmSummary.length}
          />
        </div>
        <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <CountTile label="Successful offsite copies" value={counts.successful_offsite} variant="success" />
          <CountTile label="Failed offsite copies" value={counts.failed_offsite} variant="destructive" />
          <CountTile label="Successful restores" value={counts.successful_restore} variant="success" />
          <CountTile label="Failed restores" value={counts.failed_restore} variant="destructive" />
        </div>
        {payload?.event_id_map && (
          <p className="mt-4 text-xs text-muted-foreground">
            Event IDs:{' '}
            {Object.entries(ALTARO_EVENT_IDS)
              .map(([id, label]) => `${id} (${label})`)
              .join(' · ')}
          </p>
        )}
      </CollapsePanel>

      <CollapsePanel
        id="altaro-backup-vms"
        title="Guest VM summary"
        collapsedSummary={`${vmSummary.length} VM(s) in the lookback window`}
        open={openSections.vms}
        onOpenChange={(open) => setSectionOpen('vms', open)}
      >
        <VmSummaryTable rows={vmSummary} />
      </CollapsePanel>

      <CollapsePanel
        id="altaro-backup-events"
        title="Recent events"
        collapsedSummary={
          statusFilter === 'all'
            ? `${recentEvents.length} event(s) in snapshot`
            : `${filteredEvents.length} ${statusFilter} event(s) (filtered)`
        }
        open={openSections.events}
        onOpenChange={(open) => setSectionOpen('events', open)}
      >
        {statusFilter !== 'all' && (
          <p className="mb-3 text-xs text-muted-foreground">
            Filter: {altaroStatusLabel(statusFilter)}.{' '}
            <button
              type="button"
              className="underline hover:text-foreground"
              onClick={() => setStatusFilter('all')}
            >
              Clear filter
            </button>
          </p>
        )}
        <RecentEventsList events={filteredEvents} />
      </CollapsePanel>

      <CollapsePanel
        id="altaro-backup-checks"
        title="Health checks"
        collapsedSummary={`${checks.length} check(s)`}
        open={openSections.checks}
        onOpenChange={(open) => setSectionOpen('checks', open)}
      >
        <ChecksTable checks={checks} />
      </CollapsePanel>
    </div>
  )
}
